/**
 * Dynamic Loyalty Points Component
 * Calculates and displays loyalty points based on current product price
 * Handles real-time calculation of loyalty points based on actual customer price
 *
 * Point Multipliers:
 * - Automatically retrieved from LoyaltyLion SDK (window.loyaltylion.program.rules)
 * - Falls back to hardcoded constants if SDK unavailable
 * - Tier-specific multipliers configured in LoyaltyLion admin panel
 *
 * Currency Conversion:
 * - Uses Shopify's live conversion rates (window.Shopify.currency.rate)
 * - Falls back to approximate rates if Shopify data unavailable
 *
 * Note: This component does NOT use Loyalty Lion SDK elements to avoid React DOM conflicts.
 * It calculates points independently by reading tier multipliers from the SDK data.
 */

// Fallback currency conversion rates (EUR base currency)
// These are approximate rates used only when Shopify's live rates are unavailable
// Primary conversion uses window.Shopify.currency.rate for accuracy
const FALLBACK_CURRENCY_RATES = {
  'EUR': 1.0,
  'SEK': 0.087,  // 1 SEK ≈ 0.087 EUR
  'USD': 0.92,   // 1 USD ≈ 0.92 EUR
  'GBP': 1.17,   // 1 GBP ≈ 1.17 EUR
  'DKK': 0.134,  // 1 DKK ≈ 0.134 EUR
  'NOK': 0.086,  // 1 NOK ≈ 0.086 EUR
  'CHF': 1.05,   // 1 CHF ≈ 1.05 EUR
  'PLN': 0.23,   // 1 PLN ≈ 0.23 EUR
  'CZK': 0.040,  // 1 CZK ≈ 0.040 EUR
};

// Fallback tier multipliers (points per EUR)
// These are used only when LoyaltyLion SDK is unavailable
// Primary values retrieved from window.loyaltylion.program.rules
const FALLBACK_TIER_MULTIPLIERS = {
  'Default': 5,    // Guests and customers without a tier
  'Essential': 5,  // Essential tier
  'Advanced': 6,   // Advanced tier
  'Pro': 7         // Pro tier
};

if (!customElements.get('loyalty-points')) {
  class LoyaltyPoints extends HTMLElement {
    constructor() {
      super();

      this.productId = this.dataset.productId;
      this.basePrice = parseFloat(this.dataset.basePrice); // Price in decimal format
      this.shopCurrency = this.dataset.shopCurrency; // Base shop currency (EUR)
      this.displayCurrency = this.dataset.displayCurrency; // Customer's viewing currency
      this.customerTier = this.dataset.customerTier; // Customer's tier name (Essential/Advanced/Pro)
      this.pointsElement = this.querySelector('.loyalty-points__amount');

      // Get tier name for fallback lookup (or 'Default' for guests)
      const tierName = this.customerTier || 'Default';

      // Current tier multiplier (will be set by LoyaltyLion SDK or use hardcoded fallback)
      this.tierMultiplier = FALLBACK_TIER_MULTIPLIERS[tierName] || 5;

      // Current price and quantity
      this.currentPrice = null;
      this.currentQuantity = 1;
      this.initialized = false;

      // Try to get tier multiplier from LoyaltyLion SDK
      this.initializeTierMultiplier();

      // Listen for price updates from tiered pricing
      // The price-updated event includes the correct combined quantity (cart + selector - 1)
      // for both tier calculation and points display
      document.addEventListener('price-updated', this.handlePriceUpdate.bind(this));

      // Wait for tiered pricing to initialize and dispatch first event
      // If no event received after 100ms, fallback to base price
      // This ensures we use the correct tiered/variant price instead of base price
      setTimeout(() => {
        if (!this.initialized) {
          this.updatePrice(this.basePrice, 1);
        }
      }, 100);
    }

    /**
     * Handle price update event from tiered pricing
     * @param {CustomEvent} event - Event containing new price details
     */
    handlePriceUpdate(event) {
      const { productId, priceInDecimal, selectorQuantity } = event.detail;

      // Only process events for this product
      if (productId && productId !== this.productId) {
        return;
      }

      if (priceInDecimal && priceInDecimal > 0) {
        this.initialized = true;
        // Use selectorQuantity (raw input value) for points calculation
        // This shows points for what user is adding to cart, not total including cart items
        this.updatePrice(priceInDecimal, selectorQuantity || this.currentQuantity);
      }
    }

    /**
     * Update the price and calculate loyalty points
     * @param {number} priceInDecimal - Price in decimal format per item (e.g., 29.95)
     * @param {number} quantity - Number of items
     */
    updatePrice(priceInDecimal, quantity) {
      this.currentPrice = priceInDecimal;
      this.currentQuantity = quantity || this.currentQuantity;

      // Calculate and display points based on tier
      this.updatePoints();
    }

    /**
     * Initialize tier multiplier from LoyaltyLion SDK or use fallback
     */
    initializeTierMultiplier() {
      // Check if LoyaltyLion SDK is already loaded
      if (this.tryGetTierMultiplierFromSDK()) {
        return;
      }

      // Wait for LoyaltyLion SDK to load (check every 100ms for up to 5 seconds)
      let attempts = 0;
      const maxAttempts = 50;
      const checkInterval = setInterval(() => {
        if (this.tryGetTierMultiplierFromSDK()) {
          clearInterval(checkInterval);
        } else if (attempts++ >= maxAttempts) {
          console.warn('LoyaltyLion SDK not available, using fallback tier multiplier');
          clearInterval(checkInterval);
        }
      }, 100);
    }

    /**
     * Try to get tier multiplier from LoyaltyLion SDK
     * @returns {boolean} True if successfully retrieved multiplier from SDK
     */
    tryGetTierMultiplierFromSDK() {
      if (!window.loyaltylion || !window.loyaltylion.program || !window.loyaltylion.program.rules) {
        return false;
      }

      try {
        // Find the purchase rule
        const purchaseRule = window.loyaltylion.program.rules.find(r => r.kind === 'purchase');
        if (!purchaseRule || !purchaseRule.configurations) {
          return false;
        }

        // Get tier name (or use 'Default' for guests)
        const tierName = this.customerTier || 'Default';

        // Build tier ID map
        const tierIdMap = {};
        if (window.loyaltylion.program.loyaltyTiers) {
          window.loyaltylion.program.loyaltyTiers.forEach(tier => {
            tierIdMap[tier.id] = tier.name;
          });
        }

        // Find matching configuration for this tier
        let multiplier = null;

        if (tierName === 'Default') {
          // Guest user - find config with tierId: null
          const defaultConfig = purchaseRule.configurations.find(c => c.tierId === null);
          if (defaultConfig) {
            multiplier = defaultConfig.value;
          }
        } else {
          // Logged-in user with tier - find config matching tier name
          for (const config of purchaseRule.configurations) {
            if (config.tierId && tierIdMap[config.tierId] === tierName) {
              multiplier = config.value;
              break;
            }
          }
        }

        if (multiplier !== null) {
          this.tierMultiplier = multiplier;
          console.log(`LoyaltyLion: Using tier multiplier ${multiplier} points/€ for tier "${tierName}"`);

          // Recalculate points with new multiplier if already initialized
          if (this.initialized) {
            this.updatePoints();
          }

          return true;
        }
      } catch (error) {
        console.warn('Error reading LoyaltyLion tier multiplier:', error);
      }

      return false;
    }

    /**
     * Convert price from display currency to shop base currency using Shopify's conversion rate
     * @param {number} priceInDisplayCurrency - Price in the customer's viewing currency
     * @returns {number} Price converted to base currency
     */
    convertToBaseCurrency(priceInDisplayCurrency) {
      // If already in shop currency or no conversion needed, return as-is
      if (!this.displayCurrency || this.displayCurrency === this.shopCurrency) {
        return priceInDisplayCurrency;
      }

      // Try to use Shopify's currency conversion rate
      // window.Shopify.currency.rate represents: 1 base_currency = X display_currency
      // So to convert back: display_currency / rate = base_currency
      if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
        const rate = parseFloat(window.Shopify.currency.rate);
        if (!isNaN(rate) && rate > 0) {
          return priceInDisplayCurrency / rate;
        }
      }

      // Fallback to approximate conversion rates if Shopify rate unavailable
      const fallbackRate = FALLBACK_CURRENCY_RATES[this.displayCurrency];
      if (!fallbackRate) {
        console.warn(`Loyalty points: Unknown currency ${this.displayCurrency}, using display price as-is`);
        return priceInDisplayCurrency;
      }

      return priceInDisplayCurrency * fallbackRate;
    }

    /**
     * Calculate and update loyalty points display
     * Points are ALWAYS calculated based on base currency value, regardless of display currency
     * Points = base_currency_price × quantity × tier_multiplier (rounded up)
     * Example: 280 SEK (≈€25.20) × 2 items × 10 points/€ = 504 points (using Shopify rate: 280 / 11.11 = 25.20)
     */
    updatePoints() {
      if (this.pointsElement && this.currentPrice) {
        // Convert display price to base currency for points calculation
        const baseCurrencyPrice = this.convertToBaseCurrency(this.currentPrice);

        // Calculate points based on base currency value
        const totalPoints = Math.ceil(baseCurrencyPrice * this.currentQuantity * this.tierMultiplier);
        this.pointsElement.textContent = totalPoints;
      }
    }
  }

  customElements.define('loyalty-points', LoyaltyPoints);
}
